import React, {useState, useEffect, useMemo} from "react";
import {Modal, Button, Form} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import Select from "react-select";
import {dashboardAPI} from "../../../services/api";
import {mockStores} from "../../../data/stores";

export const AssignStores = ({ show, user, onClose, onSave }: any) => {
    const {t} = useTranslation();
    const [stores, setStores] = useState<any[]>([]);
    const [storeGroups, setStoreGroups] = useState<any[]>([]);
    const [selectedStores, setSelectedStores] = useState<any[]>([]);
    const [selectedStoreGroups, setSelectedStoreGroups] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isStoreManager = user?.role === 'ROLE_STORE_MANAGER' || user?.role === 'store_manager';
    const isAreaManager = user?.role === 'ROLE_AREA_MANAGER' || user?.role === 'area_manager';
    
    // Check if demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        if (show && user) {
            setIsLoading(true);
            setError(null);
            
            if (isDemoUser) {
                // Use mock data for demo
                setStores(mockStores);
                
                // Set selected stores if user has storeIds
                if (user.storeIds && user.storeIds.length > 0) {
                    const selected = mockStores
                        .filter((s: any) => user.storeIds.includes(s.id))
                        .map((s: any) => ({
                            value: s.id,
                            label: `${s.name || s.id}${s.city ? ` (${s.city})` : ''}`
                        }));
                    setSelectedStores(selected);
                }
                
                // Mock store groups for demo
                const demoStoreGroups = [
                    { id: 'group-1', name: 'Istanbul Bölgesi' },
                    { id: 'group-2', name: 'Ankara Bölgesi' },
                    { id: 'group-3', name: 'İzmir Bölgesi' }
                ];
                
                if (isAreaManager) {
                    setStoreGroups(demoStoreGroups);
                    
                    // Set selected store groups if user has storeGroupIds
                    if (user.storeGroupIds && user.storeGroupIds.length > 0) {
                        const selected = demoStoreGroups
                            .filter((g: any) => user.storeGroupIds.includes(g.id))
                            .map((g: any) => ({
                                value: g.id,
                                label: g.name || g.id
                            }));
                        setSelectedStoreGroups(selected);
                    }
                }
                
                setIsLoading(false);
            } else {
                // Fetch stores
                dashboardAPI.getStores().then((response: any) => {
                    const storesData = Array.isArray(response) ? response : (response?.data || []);
                    setStores(storesData);
                    
                    // Set selected stores if user has storeIds
                    if (user.storeIds && user.storeIds.length > 0) {
                        const selected = storesData
                            .filter((s: any) => user.storeIds.includes(s.id))
                            .map((s: any) => ({
                                value: s.id,
                                label: `${s.name || s.id}${s.city ? ` (${s.city})` : ''}`
                            }));
                        setSelectedStores(selected);
                    }
                }).catch((err: any) => {
                    console.error('Failed to fetch stores:', err);
                    setError(err?.message || 'Failed to fetch stores');
                });
                
                // Fetch store groups if area manager
                if (isAreaManager) {
                    dashboardAPI.getStoreGroups().then((response: any) => {
                        const groupsData = Array.isArray(response) ? response : (response?.data || []);
                        setStoreGroups(groupsData);
                        
                        // Set selected store groups if user has storeGroupIds
                        if (user.storeGroupIds && user.storeGroupIds.length > 0) {
                            const selected = groupsData
                                .filter((g: any) => user.storeGroupIds.includes(g.id))
                                .map((g: any) => ({
                                    value: g.id,
                                    label: g.name || g.id
                                }));
                            setSelectedStoreGroups(selected);
                        }
                    }).catch((err: any) => {
                        console.error('Failed to fetch store groups:', err);
                        // Don't set error for store groups as it might not be implemented yet
                    });
                }
                
                setIsLoading(false);
            }
        } else {
            setSelectedStores([]);
            setSelectedStoreGroups([]);
            setError(null);
        }
    }, [show, user, isAreaManager, isDemoUser]);

    const storeOptions = useMemo(() => {
        return stores.map((store: any) => ({
            value: store.id,
            label: `${store.name || store.id}${store.city ? ` (${store.city})` : ''}`
        }));
    }, [stores]);

    const storeGroupOptions = useMemo(() => {
        return storeGroups.map((group: any) => ({
            value: group.id,
            label: group.name || group.id
        }));
    }, [storeGroups]);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);

        try {
            if (isStoreManager) {
                if (selectedStores.length === 0) {
                    setError(t('UserList.validation_select_stores') || 'Please select at least one store');
                    setIsSubmitting(false);
                    return;
                }
                await onSave({
                    storeIds: selectedStores.map((s: any) => s.value)
                });
            } else if (isAreaManager) {
                if (selectedStoreGroups.length === 0) {
                    setError(t('UserList.validation_select_store_groups') || 'Please select at least one store group');
                    setIsSubmitting(false);
                    return;
                }
                await onSave({
                    storeGroupIds: selectedStoreGroups.map((g: any) => g.value)
                });
            }
        } catch (error: any) {
            setError(error?.message || t('UserList.validation_failed') || 'Validation failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} backdrop="static" centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {isStoreManager 
                        ? (t('UserList.assign_stores') || 'Assign Stores')
                        : (t('UserList.assign_store_groups') || 'Assign Store Groups')
                    }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                {isLoading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <Form>
                        {isStoreManager && (
                            <Form.Group className="mb-3">
                                <Form.Label>{t('UserList.select_stores') || 'Select Stores'} *</Form.Label>
                                <Select
                                    options={storeOptions}
                                    value={selectedStores}
                                    onChange={(selected: any) => setSelectedStores(selected || [])}
                                    isMulti
                                    placeholder={t('UserList.select_stores_placeholder') || 'Select stores...'}
                                />
                            </Form.Group>
                        )}
                        
                        {isAreaManager && (
                            <Form.Group className="mb-3">
                                <Form.Label>{t('UserList.select_store_groups') || 'Select Store Groups'} *</Form.Label>
                                <Select
                                    options={storeGroupOptions}
                                    value={selectedStoreGroups}
                                    onChange={(selected: any) => setSelectedStoreGroups(selected || [])}
                                    isMulti
                                    placeholder={t('UserList.select_store_groups_placeholder') || 'Select store groups...'}
                                />
                                <Form.Text className="text-muted">
                                    {t('UserList.store_groups_info') || 'Store groups will be automatically resolved to individual stores.'}
                                </Form.Text>
                            </Form.Group>
                        )}
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="dark" onClick={onClose} disabled={isSubmitting}>
                    {t('RuleList.cancel') || 'Cancel'}
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting || isLoading}>
                    {isSubmitting ? (t('RuleList.saving') || 'Saving...') : (t('RuleList.save') || 'Save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

